import { useState } from 'react'
import type { VolumeRangeKey } from '../../api/types'
import { toLb } from '../../lib/units'
import { useUnits } from '../../lib/UnitsProvider'
import { AsyncPanel, PanelSkeleton, Shimmer } from '../ui/AsyncPanel'
import { Panel, TOKENS } from '../ui/dashboard-primitives'
import { HIT_44 } from '../ui/hitArea'
import { CountUp } from './CountUp'
import { useRangeCountersPanel } from './panelStates'

const RANGES: readonly { key: VolumeRangeKey; label: string; days: string }[] = [
  { key: '7d', label: '7 days', days: 'training days in 7' },
  { key: '30d', label: '30 days', days: 'training days in 30' },
  { key: '90d', label: '90 days', days: 'training days in 90' },
  { key: 'total', label: 'Total', days: 'training days in total' },
]

function sleepText(min: number | null): string {
  if (min == null) return '·'
  return `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')}`
}

function Counter({
  k,
  children,
  sub,
}: {
  k: string
  children: React.ReactNode
  sub: string
}) {
  return (
    <div>
      <span
        className="text-[0.5625rem] tracking-[0.28em] uppercase"
        style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamDim }}
      >
        {k}
      </span>
      <p
        className="mt-3 tabular-nums leading-none"
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 500,
          fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
          color: TOKENS.cream,
        }}
      >
        {children}
      </p>
      <p className="mt-2 text-[0.8125rem]" style={{ color: TOKENS.creamDim }}>
        {sub}
      </p>
    </div>
  )
}

function unit(label: string) {
  return (
    <s
      className="no-underline ml-1.5 text-[0.42em]"
      style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamFaint, letterSpacing: '0.04em' }}
    >
      {label}
    </s>
  )
}

export function RangeCounters() {
  const counters = useRangeCountersPanel()
  const { load } = useUnits()
  const [range, setRange] = useState<VolumeRangeKey>('7d')
  const active = RANGES.find((r) => r.key === range) ?? RANGES[0]

  // Sessions, tonnage and working sets are GET /api/volume's: the one
  // calculation, so a day inside the range reads the same kilograms on the
  // Sessions page. Sleep is the only figure the windows endpoint still owns.
  const v = counters.data?.volume.ranges.find((x) => x.range === range)
  const sleep = counters.data?.windows.windows.find((x) => x.range === range)
  // CountUp animates a raw number, so it can't take a formatted string the
  // way every other load render in this task does — convert the underlying
  // kg value via useUnits().load (never a guessed unit), same pattern
  // format.ts's own tonnage() uses internally for the 'lb' branch.
  const tonnageKg = v?.tonnage_kg ?? 0
  const tonnageDisplay = load === 'lb' ? toLb(tonnageKg) : tonnageKg

  return (
    <section className="relative py-10 lg:py-14">
      <div className="flex items-center justify-between gap-6 flex-wrap mb-8">
        <div className="flex items-baseline gap-3">
          <span
            className="text-[0.625rem] tracking-[0.32em]"
            style={{ fontFamily: 'var(--font-mono)', color: TOKENS.amber }}
          >
            02
          </span>
          <span className="text-[0.625rem]" style={{ color: TOKENS.creamFaint }}>
            /
          </span>
          <span
            className="text-[0.625rem] tracking-[0.28em] uppercase"
            style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamDim }}
          >
            Volume · rolling window
          </span>
        </div>

        {/* Only while there are counters to switch: over a skeleton or an
            error the toggle would switch nothing. */}
        {counters.state === 'ready' && (
          <div
            className="inline-flex p-[3px] gap-0.5 rounded-full border"
            role="group"
            aria-label="Time range"
            style={{ borderColor: TOKENS.creamBorder, background: TOKENS.creamHairline }}
          >
            {RANGES.map((r) => {
              const pressed = r.key === range
              return (
                <button
                  key={r.key}
                  type="button"
                  aria-pressed={pressed}
                  onClick={() => setRange(r.key)}
                  className={`${HIT_44} px-4 py-[7px] rounded-full text-[0.625rem] tracking-[0.18em] uppercase transition-colors`}
                  style={{
                    fontFamily: 'var(--font-mono)',
                    color: pressed ? TOKENS.amber : TOKENS.creamDim,
                    // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
                    background: pressed ? 'rgba(232,197,160,0.16)' : 'transparent',
                  }}
                >
                  {r.label}
                </button>
              )
            })}
          </div>
        )}
      </div>

      <AsyncPanel
        sources={counters.sources}
        hasContent={counters.hasContent}
        subject="Your training volume"
        skeleton={
          <PanelSkeleton label="Loading training volume">
            <Shimmer height={132} />
          </PanelSkeleton>
        }
      >
        {() => (
          <Panel variant="light" className="grid grid-cols-2 lg:grid-cols-4 gap-10">
            <Counter k="Sessions" sub={active.days}>
              <CountUp value={v?.session_days ?? 0} />
            </Counter>
            <Counter k="Tonnage" sub="all training">
              <CountUp value={tonnageDisplay} comma />
              {unit(load)}
            </Counter>
            <Counter k="Working sets" sub="reps actually logged">
              <CountUp value={v?.working_sets ?? 0} />
            </Counter>
            <Counter k="Sleep" sub="nightly average">
              {sleepText(sleep?.avg_sleep_min ?? null)}
              {unit('avg')}
            </Counter>
          </Panel>
        )}
      </AsyncPanel>
    </section>
  )
}
